import React, { useState } from 'react';
import './FordCreditCarCards.scss';
import Countdown from '../Countdown';
import { Pagination } from 'antd';
import { UpOutlined, DownOutlined, StarOutlined, StarFilled, ShoppingCartOutlined } from "@ant-design/icons"; 
import { toggleFavorite } from '../../utils/toggleFavorites';
import { Car } from '../../types/home';
import { useNavigate } from 'react-router-dom';

interface CarCardProps {
    page: number;
    itemsPerPage: number;
    dataSource: Array<Car>;
    carsLength: number;
    onPageChange: (page: number) => void;
    setDataSource: (cars: Array<Car>) => void;
    starToggle: string;
    setStarChanged: (starChanged: boolean) => void;
    onNavigateToDetails: () => void;
    pages: boolean; // Determines if pagination should be shown
}

const FordCreditCarCards: React.FC<CarCardProps> = ({ 
    page, 
    itemsPerPage, 
    dataSource, 
    carsLength, 
    onPageChange, 
    setDataSource, 
    starToggle, 
    setStarChanged, 
    onNavigateToDetails,
    pages
}) => {
    const [currentPage, setCurrentPage] = useState(page);
    const navigate = useNavigate();

    const handleTableChange = (pagination: number) => {
        setCurrentPage(pagination);
        onPageChange(pagination);
    };

    const handleViewClick = (car: Car) => {
        //onNavigateToDetails();
        //const encodedKey = encodeURIComponent(car.key);
        //navigate(`/car/${encodedKey}`, { state: { car: car } });
    };

    const toggleStar = (name: string) => {
        const updatedDataSource = dataSource.map((car) => {
            if (car.name === name) {
                toggleFavorite({ link: car.link, favorited: !car.Favorited });
                return { ...car, Favorited: !car.Favorited };
            }
            return car;
        });
        if (starToggle === "starred") {
            setStarChanged(true);
        }
        setDataSource(updatedDataSource);
    };

    const CarTags: React.FC<{ car: Car }> = ({ car }) => {
        const isNumeric = (value: any) => {
            if (typeof value === 'string') {
                value = value.replace(/[^0-9.-]+/g, '');
            }
            return !isNaN(parseFloat(value)) && isFinite(value);
        };
        
        const cleanNumericValue = (value: string) => parseFloat(value.replace(/[^0-9.-]+/g, ''));
        
        const currentBidValue = isNumeric(car.currentBid) ? cleanNumericValue(car.currentBid) : null;
        const averagePriceValue = isNumeric(car.AveragePrice) ? cleanNumericValue(car.AveragePrice) : null;
        const isBidHigher = currentBidValue !== null && averagePriceValue !== null && currentBidValue > averagePriceValue;
        const isInvalid = currentBidValue === null || averagePriceValue === null;
        const difference = (averagePriceValue && currentBidValue) ? averagePriceValue - currentBidValue : null;
        const formattedDifference = difference !== null 
            ? `$${difference.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` 
            : null;

        return (
            <div className="car-tags">
                <span className="car-tag current-bid">
                    Asking Price: {car.currentBid}
                </span>
                {!isInvalid && (
                    <span className={`car-tag average-price ${isBidHigher ? 'higher-bid' : 'lower-bid'}`}>
                        {isBidHigher ? <DownOutlined className='arrow-down' /> : <UpOutlined className='arrow-up' />}
                        Avg Retail: {car.AveragePrice}
                    </span>
                )}
                {!isInvalid && (
                    <span className={`car-tag difference ${isBidHigher ? 'higher-bid' : 'lower-bid'}`}>
                        Margin: {formattedDifference}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="car-cards-container">
            {dataSource.map((car) => (
                <div className="car-card" key={car.key}>
                    <div className="image-container">
                        <img src={car.image} alt={car.name} />
                        <div className="FordCredit-label">
                            Ford Credit
                        </div>
                        {(car.heat_score !== undefined && !isNaN(car.heat_score)) && (
                                <div className="heat-score-container">
                                    <div className="heat-score">
                                    {car.heat_score === 0 && "Inventory Gap"}
                                    {car.heat_score === 1 && "High Demand"}
                                    {car.heat_score === 2 && "CRM Match"}
                                    {car.heat_score === 3 && "Past Sales Match"}
                                    </div>
                                    <div className="heat-score-tooltip">
                                    {car.heat_score === 0 && 
                                    "This vehicle represents an inventory gap in your dealership. Adding this vehicle could help diversify your inventory offerings."}
                                    {car.heat_score === 1 && 
                                    "This vehicle is in high demand in your market area. Data shows strong consumer interest and potentially quick turnover for this model."}
                                    {car.heat_score === 2 && 
                                    "This vehicle matches interests from customers in your CRM. There are potential buyers already in your database who have shown interest in similar vehicles."}
                                    {car.heat_score === 3 && 
                                    "This vehicle is similar to models that have sold well at your dealership in the past. Historical sales data suggests this would be a good addition."}
                                    </div>
                                </div>
                                )}
                    </div>
                    <div className="car-details">
                        <div className="name-row">
                            <h3 className="car-name">{car.name}</h3>
                            <span className="website-name">Ford Credit</span>
                        </div>
                        <CarTags car={car} />
                        <p className="car-location">Location: {car.location}</p>
                        <div className="container">
                            <span
                                className="star-icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleStar(car.name);
                                }}
                            >
                                {car.Favorited ? (
                                    <ShoppingCartOutlined
                                        // style={{ color: "gold" }}
                                        className="cart-icon active"
                                    />
                                ) : (
                                    <ShoppingCartOutlined />
                                )}
                            </span>
                            <button
                                className="view-button"
                                onClick={() => handleViewClick(car)}
                            >
                                View Details
                            </button>
                        </div>
                    </div>
                </div>
            ))}
            
            {pages && (
                <Pagination
                    className="custom-pagination"
                    current={page}
                    pageSize={itemsPerPage}
                    total={carsLength}
                    onChange={handleTableChange}
                    showSizeChanger={false}
                />
            )}
        </div>
    );
};

export default FordCreditCarCards;
